import { FC, FormEvent, useState } from 'react';
import informerLogo from '../../../../../../../../assets/images/logo_black.png';
import { Checkbox } from '../../../../../../../../components/checkbox/checkbox';
import { Container } from '../../../../../../../../components/container/container';
import { InputFileSquare } from '../../../../../../../../components/input-file/input-file-square/input-file-square';
import { Input } from '../../../../../../../../components/input/input';
import { Loader } from '../../../../../../../../components/loader/loader';
import { Select } from '../../../../../../../../components/select/select';
import { MultiSelect } from '../../../../../../../../features/multi-select/multi-select';
import { NestedIdValue } from '../../../../../../../../features/multi-select/use-multi-select';
import { IdValue } from '../../../../../../../../types/props';
import { qrTextPlacements, qrVariants } from '../helpers';
import s from './print-settings.module.css';
import { usePrintSettings } from './use-print-settings';

interface Props {
  codes: {
    onChange: (codes: NestedIdValue[]) => void;
    value: NestedIdValue[];
  };
  size: {
    onChange: (size: number) => void;
    value: number;
  };
  template: {
    onChange: (template: IdValue) => void;
    value?: IdValue;
  };
  variant: {
    onChange: (variant: IdValue) => void;
    value?: IdValue;
    onUploadLogo?: (file?: File) => void;
    logo?: string;
  };
  duplicate: {
    onChange: (duplicate: string) => void;
    value: string;
  };
  text: {
    onChange: (template: IdValue) => void;
    value?: IdValue;
  };
  from: {
    onChange: (from: number) => void;
    value: number;
  };
  background: {
    onChange: (background: string) => void;
    value: string;
  };
  dots: {
    onChange: (dots: string) => void;
    value: string;
  };
  textColor: {
    onChange: (textColor: string) => void;
    value: string;
  };
}
export const PrintSettings: FC<Props> = (props) => {
  const { codes, size, template, variant, duplicate, text, from, background, dots, textColor } =
    props;

  const { groupedQrCodesByBranch, isLoading, t, templates } = usePrintSettings();
  const localizedVariants = qrVariants.map((variant) => ({ ...variant, value: t(variant.value) }));
  const localizedTextPlacements = qrTextPlacements.map((placement) => ({
    ...placement,
    value: t(placement.value),
  }));
  const localizedTemplates = templates.map((template) => ({
    ...template,
    value: t(template.value),
  }));

  const [lastBg, setLastBg] = useState<string>(background.value);
  const [lastDots, setLastDots] = useState<string>(dots.value);
  const [lastTextColor, setLastTextColor] = useState<string>(textColor.value);

  const handleChangeBackground = (e: FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    background.onChange(value);
    setLastBg(value);
  };

  const handleChangeDots = (e: FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    dots.onChange(value);
    setLastDots(value);
  };

  const handleChangeTextColor = (e: FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    textColor.onChange(value);
    setLastTextColor(value);
  };

  const isBgTransparent = background.value === 'transparent';
  const isDotsTransparent = dots.value === 'transparent';
  const isTextColorTransparent = textColor.value === 'transparent';

  return (
    <div className={s.feature}>
      {isLoading && <Loader />}
      <Container label={t('QR-коды')}>
        <MultiSelect
          options={groupedQrCodesByBranch}
          onSelect={codes.onChange}
          value={codes.value}
        />
      </Container>

      <Container label={t('Вид')}>
        <Select
          options={localizedVariants}
          onSelect={variant.onChange}
          value={variant.value || localizedVariants[0]}
          wrap="wrap"
        />
      </Container>

      {variant.value?.id === 'logo' && (
        <InputFileSquare
          onChange={(e) => {
            variant.onUploadLogo?.(e?.[0]);
          }}
          types={['image/*']}
          src={variant.logo || informerLogo}
          className={s.logo}
          deletable={!!variant.logo}
        />
      )}

      <Container label={t('Текст')}>
        <Select
          options={localizedTextPlacements}
          onSelect={text.onChange}
          value={text.value || localizedTextPlacements[0]}
          wrap="wrap"
        />
      </Container>

      <Container label={t('Шаблон')}>
        <Select
          options={localizedTemplates}
          onSelect={template.onChange}
          value={template.value || localizedTemplates[0]}
          wrap="wrap"
        />
      </Container>

      <Container label={t('Копий')}>
        <Input
          value={duplicate.value}
          type="number"
          max={100}
          onChange={(e) => duplicate.onChange(e.currentTarget.value)}
        />
      </Container>

      <Container label={t('Начиная с')}>
        <Input
          value={from.value}
          type="number"
          onChange={(e) => from.onChange(Number(e.currentTarget.value))}
        />
      </Container>

      <div className={s.colors}>
        <Container label={t('Цвет фона QR-кода')}>
          <Container isHorizontal>
            <input type="color" value={background.value} onChange={handleChangeBackground} />
            <Checkbox
              onClick={() => background.onChange(isBgTransparent ? lastBg : 'transparent')}
              checked={isBgTransparent}
              label={t('Прозрачный')}
            />
          </Container>
        </Container>

        <Container label={t('Цвет точек QR-кода')}>
          <Container isHorizontal>
            <input type="color" value={dots.value} onChange={handleChangeDots} />
            <Checkbox
              onClick={() => dots.onChange(isDotsTransparent ? lastDots : 'transparent')}
              checked={isDotsTransparent}
              label={t('Прозрачный')}
            />
          </Container>
        </Container>

        <Container label={t('Цвет текста')}>
          <Container isHorizontal>
            <input type="color" value={textColor.value} onChange={handleChangeTextColor} />
            <Checkbox
              onClick={() =>
                textColor.onChange(isTextColorTransparent ? lastTextColor : 'transparent')
              }
              checked={isTextColorTransparent}
              label={t('Прозрачный')}
            />
          </Container>
        </Container>
      </div>

      <div className={s.size}>
        <Container label={t('Размер QR-кода')}>
          <Input
            value={size.value}
            type="range"
            onChange={(e) => size.onChange(Number(e.currentTarget.value))}
            min={0}
            max={100}
            step={0.01}
          />
        </Container>

        <Input
          value={size.value}
          type="number"
          onChange={(e) => size.onChange(Number(e.currentTarget.value))}
          min={0}
          size="sm"
          max={100}
          step={0.01}
        />
      </div>
    </div>
  );
};
